import React from 'react';
import { CheckCircle, ExternalLink, Plus } from 'lucide-react';
import LogoString from '../../assets/images/logo_string.svg';
import { useSendMoney } from '@/contexts/SendMoneyContext';

interface TransactionCompleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNewSettlement: () => void;
  transactionData: {
    transactionId: string;
    duration: number;
    amount: number;
    senderCompany: string;
    receiverCompany: string;
    protocolFee: number;
    legacyCost: number;
    savings: number;
    escrowResult?: any;
    isEscrowFlow?: boolean;
  };
  exchangeRate: number;
}

const TransactionCompleteModal: React.FC<TransactionCompleteModalProps> = ({
  isOpen,
  onClose,
  onNewSettlement,
  transactionData,
  exchangeRate
}) => {
  if (!isOpen) return null;

  const {slot} = useSendMoney();

  const savingsPercentage = ((transactionData.savings / transactionData.legacyCost) * 100).toFixed(1);

  const handleViewOnSolscan = () => {
    if (transactionData.escrowResult?.commitHoldSignature) {
      const solscanUrl = `https://solscan.io/tx/${transactionData.escrowResult.commitHoldSignature}?cluster=devnet`;
      window.open(solscanUrl, '_blank');
    } else {
      const solscanUrl = `https://solscan.io/tx/${transactionData.escrowResult.voidHoldSignature}?cluster=devnet`;
      window.open(solscanUrl, '_blank');
    }
  };

  const handleViewHoldOnSolscan = () => {
    if (transactionData.escrowResult?.createHoldSignature) {
      const solscanUrl = `https://solscan.io/tx/${transactionData.escrowResult.createHoldSignature}?cluster=devnet`;
      window.open(solscanUrl, '_blank');
    }
  };

  const handleViewCommitOnSolscan = () => {
    if (transactionData.escrowResult?.commitHoldSignature) {
      const solscanUrl = `https://solscan.io/tx/${transactionData.escrowResult.commitHoldSignature}?cluster=devnet`;
      window.open(solscanUrl, '_blank');
    }
  };

  const handleViewVoidOnSolscan = () => {
    if (transactionData.escrowResult?.voidHoldSignature) {
      const solscanUrl = `https://solscan.io/tx/${transactionData.escrowResult.voidHoldSignature}?cluster=devnet`;
      window.open(solscanUrl, '_blank');
    }
  };

  return (
    <div className="fixed top-0 left-0 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto h-auto w-full z-50 flex items-center justify-center">
      <div className="relative flex flex-col justify-start items-center mx-auto px-[10%] pt-12 w-full max-w-[80%] h-full shadow-lg rounded-md bg-[#0E131A]">
        <div className="absolute top-12 left-12">
          <img src={LogoString} alt="Logo" className="h-[26px] w-auto" />
        </div>
        
        <button 
          onClick={onClose} 
          className="text-gray-400 hover:text-gray-300 absolute top-4 right-4"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Success Icon */}
        <div className="relative mb-8">
          <div className="w-32 h-32 bg-gray-600 rounded-lg flex items-center justify-center opacity-50">
            <CheckCircle className="h-16 w-16 text-green-400" />
          </div>
          {/* Floating particles */}
          <div className="absolute -top-2 -right-2 w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
          <div className="absolute top-4 -left-4 w-2 h-2 bg-green-300 rounded-full animate-pulse delay-300"></div>
          <div className="absolute -bottom-2 left-4 w-2 h-2 bg-green-500 rounded-full animate-pulse delay-700"></div>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h3 className="text-[44px] font-bold text-white mb-4">Settlement Complete!</h3>
        </div>

        {/* Settlement Details Card */}
        <div className="bg-[#182233] border border-[#567095]/30 rounded-lg p-6 w-full max-w-2xl mb-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Settlement ID:</span>
              <span className="text-white font-mono">{transactionData.transactionId.slice(0, 8)}...{transactionData.transactionId.slice(-8)}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Duration:</span>
              <span className="text-blue-400 font-bold">{transactionData.duration} seconds</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Amount Settled:</span>
              <span className="text-white font-bold">${transactionData.amount.toLocaleString()}.00</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Exchange Rate:</span>
              <span className="text-white">{(exchangeRate/10000).toFixed(2)} ({(100 - exchangeRate / 100)}% spread)</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Protocol Fee:</span>
              <span className="text-white">${transactionData.protocolFee.toFixed(2)} (0.08%)</span>
            </div>
            
            {transactionData.isEscrowFlow && transactionData.escrowResult && (
              <>
                <div className="border-t border-gray-600 my-4"></div>
                <div className="text-center mb-4">
                  <span className="text-blue-400 font-semibold">Escrow Transaction Details</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Hold ID:</span>
                  <span className="text-white font-mono text-sm">{transactionData.escrowResult.holdId.slice(0, 12)}...</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Final Status:</span>
                  <span className={`font-semibold ${
                    transactionData.escrowResult.finalStatus === 'COMMITTED' ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {transactionData.escrowResult.finalStatus}
                  </span>
                </div>
                
                {transactionData.escrowResult.createHoldSignature && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Create Hold:</span>
                    <button
                      onClick={handleViewHoldOnSolscan}
                      className="text-blue-400 hover:text-blue-300 flex items-center space-x-1"
                    >
                      <span className="text-sm">View on Solscan</span>
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                )}
                
                {transactionData.escrowResult.commitHoldSignature && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Commit Hold:</span>
                    <button
                      onClick={handleViewCommitOnSolscan}
                      className="text-green-400 hover:text-green-300 flex items-center space-x-1"
                    >
                      <span className="text-sm">View on Solscan</span>
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                )}
                
                {transactionData.escrowResult.voidHoldSignature && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Void Hold:</span>
                    <button
                      onClick={handleViewVoidOnSolscan}
                      className="text-red-400 hover:text-red-300 flex items-center space-x-1"
                    >
                      <span className="text-sm">View on Solscan</span>
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </>
            )}
            
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Legacy Cost (Avoided):</span>
              <span className="text-orange-400 font-bold">${transactionData.legacyCost.toFixed(2)}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-400">You Saved:</span>
              <span className="text-blue-400 font-bold">${transactionData.savings.toFixed(2)} ({savingsPercentage}%)</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Blockchain Confirmation:</span>
              <span className="text-white">Block #{slot}</span>
            </div>
          </div>
        </div>

        {/* Total Savings Card */}
        <div className="bg-[#182233] border border-[#567095]/30 rounded-lg p-6 w-full max-w-2xl mb-8">
          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-lg">You saved:</span>
            <span className="text-white text-3xl font-bold">${transactionData.savings.toFixed(2)}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-4 mb-8">
          <button
            onClick={handleViewOnSolscan}
            className="flex items-center space-x-2 px-6 py-3 border border-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            <span>View on Solscan</span>
          </button>
          
          <button
            onClick={onNewSettlement}
            className="flex items-center space-x-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>New settlement</span>
          </button>
        </div>

        <div className="text-center text-gray-400 text-sm">
          © 2025 LoopChain
        </div>
      </div>
    </div>
  );
};

export default TransactionCompleteModal;
